import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import './store-commerce.css';
import './storefront-polish.css';
import './store-pdp.css';
import './home-hero.css';
import './store-forms.css';
import { BrowserRouter as Router, Routes, Route, Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import About from './About';
import Contact from './Contact';
import Shop from './Shop';
import React, { useState, useRef, useEffect } from 'react';
import Home from './Home';
import HomeAnnouncementBar from './HomeAnnouncementBar';
import Admin from './Admin';
import LoginPage from './Login';
import AccountPage from './Account';
import CartPage from './Cart';
import CheckoutPage from './Checkout';
import OrderSuccessPage from './OrderSuccess';
import { CartProvider, useCart } from './cart/CartContext';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { loginUrlWithReturn } from './admin/adminUtils';
import { SiteSettingsProvider, useSiteSettings } from './SiteSettingsContext';
import PostLoginWelcomeToast from './auth/PostLoginWelcome';
import CartAddedToast from './checkout/CartAddedToast';
import { ShopMegaMenu, ShopMobileNav } from './components/ShopNavMenu';
import DynamicCategoryHub from './DynamicCategoryHub';
import DynamicCategoryList from './DynamicCategoryList';
import LegacyUrlRedirect, { legacyRedirectRoutePaths } from './LegacyUrlRedirect';
import CustomJewelryPage from './jewelry/CustomJewelryPage';
import ProductSearch from './ProductSearch';
import ProductDetailPage from './ProductDetail';
import PolicyPage from './PolicyPage';
import NotFound from './NotFound';
import ForgotPasswordPage from './ForgotPassword';
import ResetPasswordPage from './ResetPassword';
import TrackOrderPage from './TrackOrder';
import OrderInvoicePage from './OrderInvoice';
import BlogPage from './Blog';
import BlogPostPage from './BlogPost';
import { WishlistProvider, useWishlist } from './wishlist/WishlistContext';
import WishlistPage from './WishlistPage';
import GiftFinderRoute from './giftFinder/GiftFinderRoute';
import { isGiftFinderLinkVisible } from './giftFinder/giftFinderSettings';
import { isCustomJewelryLinkVisible } from './jewelry/customJewelrySettings';
import ScrollToTop from './ScrollToTop';
import { brandNameFromSettings, brandLogoSrcFromSettings, getFooterCopy } from './siteBrand';
import {
  closeMobileNav,
  createSameRouteNavHandler,
  isSameNavRoute,
  parseInternalNavTarget,
  scrollToPageTop,
} from './navSameRouteClick';

function SiteHeaderAccountMenu({ isAdmin, logout, pathname, onNavLinkClick }) {
  const menuActive = pathname === '/account' || pathname.startsWith('/admin');
  return (
    <div className="dropdown site-header-account-dropdown site-header-util-tile-wrap">
      <button
        type="button"
        className={`btn btn-sm rounded-pill dropdown-toggle site-header-account-toggle site-header-util-tile ${
          menuActive ? 'btn-secondary' : 'btn-outline-secondary'
        }`}
        data-bs-toggle="dropdown"
        aria-expanded="false"
        id="headerAccountMenu"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16" aria-hidden className="site-header-util-tile__icon d-lg-none">
          <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6m2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0m4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4 1-1 1-1z" />
        </svg>
        <span className="site-header-account-toggle__text">Account</span>
      </button>
      <ul className="dropdown-menu dropdown-menu-end shadow-sm" aria-labelledby="headerAccountMenu">
        <li>
          <NavLink className="dropdown-item" to="/account" onClick={onNavLinkClick('/account')}>
            My account
          </NavLink>
        </li>
        {isAdmin ? (
          <li>
            <NavLink className="dropdown-item" to="/admin" onClick={onNavLinkClick('/admin')}>
              Catalog admin
            </NavLink>
          </li>
        ) : null}
        <li>
          <hr className="dropdown-divider" />
        </li>
        <li>
          <button type="button" className="dropdown-item" onClick={() => void logout()}>
            Sign out
          </button>
        </li>
      </ul>
    </div>
  );
}

function SiteHeaderWishlistLink({ onClick }) {
  const { count } = useWishlist();
  return (
    <NavLink
      to="/wishlist"
      className="site-header-icon-btn site-header-wishlist-btn site-header-util-tile"
      aria-label={count > 0 ? `Saved items, ${count}` : 'Open saved items'}
      onClick={onClick}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16" aria-hidden className="site-header-util-tile__icon">
        <path d="M8 2.748l-.717-.737C5.6.281 2.514.878 1.4 3.053c-.523 1.023-.641 2.5.314 3.385l5.657 5.657a.5.5 0 0 0 .708 0l5.657-5.657c.955-.885.837-2.362.314-3.385C13.486.878 10.4.28 8.717 2.01L8 2.748zm-.134 9.198 5.657-5.657a.5.5 0 0 0-.708-.708L8 10.293 4.854 7.146a.5.5 0 1 0-.708.708l5.657 5.657z" />
      </svg>
      <span className="site-header-util-tile__label d-lg-none">Wishlist</span>
      {count > 0 ? (
        <span className="site-header-cart-badge" aria-hidden>
          {count > 99 ? '99+' : count}
        </span>
      ) : null}
    </NavLink>
  );
}

function SiteHeaderCartLink({ onClick }) {
  const { itemCount } = useCart();
  return (
    <NavLink
      to="/cart"
      className="site-header-icon-btn site-header-cart-btn site-header-util-tile"
      aria-label={itemCount > 0 ? `Cart, ${itemCount} items` : 'Open cart'}
      onClick={onClick}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16" aria-hidden className="site-header-util-tile__icon">
        <path d="M0 1.5A.5.5 0 0 1 .5 1H2a.5.5 0 0 1 .485.379L2.89 3H14.5a.5.5 0 0 1 .491.592l-1.5 8A.5.5 0 0 1 13 12H4a.5.5 0 0 1-.491-.408L2.01 3.607 1.61 2H.5a.5.5 0 0 1-.5-.5zM5 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm7 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm-7-1a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm7 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
      </svg>
      <span className="site-header-util-tile__label d-lg-none">Bag</span>
      {itemCount > 0 ? (
        <span className="site-header-cart-badge" aria-hidden>
          {itemCount > 99 ? '99+' : itemCount}
        </span>
      ) : null}
    </NavLink>
  );
}

function SiteHeaderSignInLink({ pathname, search, onClick }) {
  const loginActive = pathname === '/login' || pathname.startsWith('/login');
  const loginTo = loginUrlWithReturn(pathname, search);
  return (
    <NavLink
      to={loginTo}
      title="Sign in"
      aria-label="Sign in to your account"
      onClick={onClick}
      className={({ isActive }) =>
        `btn btn-sm rounded-pill site-header-account-toggle site-header-guest-toggle site-header-sign-in-link site-header-util-tile text-decoration-none ${
          isActive || loginActive ? 'btn-secondary' : 'btn-outline-primary'
        }`
      }
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16" aria-hidden className="site-header-util-tile__icon d-lg-none">
        <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6m2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0m4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4 1-1 1-1z" />
      </svg>
      <span className="site-header-guest-toggle__label site-header-account-toggle__text">Sign in</span>
    </NavLink>
  );
}

function SiteHeaderSearch({ headerSearchQ, setHeaderSearchQ, onSubmit, onAfterSubmit }) {
  const handleSubmit = (e) => {
    onSubmit(e);
    onAfterSubmit?.();
  };

  return (
    <>
      <form
        className="site-header-search-form site-header-search-form--desktop d-none d-lg-block"
        onSubmit={handleSubmit}
        role="search"
        aria-label="Search products"
      >
        <div className="input-group site-header-search-group shadow-sm">
          <input
            type="search"
            className="form-control"
            placeholder="Search name or ID…"
            aria-label="Search by product name or ID"
            value={headerSearchQ}
            onChange={(e) => setHeaderSearchQ(e.target.value)}
            autoComplete="off"
          />
          <button type="submit" className="btn btn-primary px-3" aria-label="Search">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16" aria-hidden>
              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
            </svg>
          </button>
        </div>
      </form>
      <form
        className="site-header-search-form site-header-search-form--drawer d-lg-none"
        onSubmit={handleSubmit}
        role="search"
        aria-label="Search products"
      >
        <label className="site-header-search-drawer-label" htmlFor="mobile-menu-search">
          Search the catalog
        </label>
        <div className="input-group site-header-search-group site-header-search-group--drawer shadow-sm">
          <span className="input-group-text site-header-search-drawer-icon" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
            </svg>
          </span>
          <input
            id="mobile-menu-search"
            type="search"
            className="form-control"
            placeholder="Search…"
            aria-label="Search by product name or ID"
            value={headerSearchQ}
            onChange={(e) => setHeaderSearchQ(e.target.value)}
            autoComplete="off"
          />
          <button type="submit" className="btn btn-primary site-header-search-drawer-submit" aria-label="Search">
            <span className="site-header-search-drawer-submit__text">Search</span>
            <svg className="site-header-search-drawer-submit__icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
            </svg>
          </button>
        </div>
      </form>
    </>
  );
}

function AppInner() {
  const location = useLocation();
  const navigate = useNavigate();
  const { ready: authReady, isAuthed, isAdmin, logout } = useAuth();
  const { settings: site } = useSiteSettings();
  const brandName = brandNameFromSettings(site);
  const brandLogo = brandLogoSrcFromSettings(site);
  const footerCopy = getFooterCopy(site.page_copy);
  const store = site.store_copy || {};
  const [headerSearchQ, setHeaderSearchQ] = useState('');
  // Dropdown state for Shop (desktop hover)
  const [shopOpen, setShopOpen] = useState(false);
  const [shopCloseTimeout, setShopCloseTimeout] = useState(null);
  const shopRef = useRef();

  /** Re-tap current nav item: close mobile menu and scroll to top (no full reload). */
  const onNavLinkClick = createSameRouteNavHandler(location.pathname, {
    onBefore: closeMobileNav,
    currentSearch: location.search,
  });

  const handleMouseEnter = () => {
    if (shopCloseTimeout) {
      clearTimeout(shopCloseTimeout);
      setShopCloseTimeout(null);
    }
    setShopOpen(true);
  };

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      setShopOpen(false);
    }, 300);
    setShopCloseTimeout(timeout);
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (shopRef.current && !shopRef.current.contains(event.target)) {
        setShopOpen(false);
        if (shopCloseTimeout) {
          clearTimeout(shopCloseTimeout);
          setShopCloseTimeout(null);
        }
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (shopCloseTimeout) {
        clearTimeout(shopCloseTimeout);
      }
    };
  }, [shopCloseTimeout]);

  const headerFixedRef = useRef(null);
  const [headerCompact, setHeaderCompact] = useState(false);
  useEffect(() => {
    const onScroll = () => setHeaderCompact(window.scrollY > 48);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const el = headerFixedRef.current;
    if (!el) return undefined;
    const syncHeaderHeight = () => {
      const isAdmin = location.pathname.startsWith('/admin');
      const shell = el.querySelector('.site-header-shell');
      const fixedRect = el.getBoundingClientRect();
      let clearance = 72;
      if (shell) {
        const shellRect = shell.getBoundingClientRect();
        clearance = Math.ceil(shellRect.bottom - fixedRect.top);
      } else {
        clearance = Math.ceil(fixedRect.height);
      }
      clearance = Math.max(52, clearance);
      if (isAdmin) {
        clearance = Math.min(clearance, 76);
      }
      const blockHeight = Math.ceil(fixedRect.height);
      document.documentElement.style.setProperty('--site-header-clearance', `${clearance}px`);
      document.documentElement.style.setProperty('--site-header-height', `${blockHeight}px`);
      document.documentElement.style.setProperty('--site-header-offset', `${clearance}px`);
    };
    syncHeaderHeight();
    const ro = new ResizeObserver(syncHeaderHeight);
    ro.observe(el);
    window.addEventListener('resize', syncHeaderHeight);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', syncHeaderHeight);
    };
  }, [authReady, isAuthed, headerCompact, location.pathname]);

  useEffect(() => {
    const nav = document.getElementById('navbarNav');
    if (!nav) return undefined;
    const resync = () => {
      window.requestAnimationFrame(() => {
        window.dispatchEvent(new Event('resize'));
      });
    };
    nav.addEventListener('shown.bs.collapse', resync);
    nav.addEventListener('hidden.bs.collapse', resync);
    return () => {
      nav.removeEventListener('shown.bs.collapse', resync);
      nav.removeEventListener('hidden.bs.collapse', resync);
    };
  }, []);

  useEffect(() => {
    setShopOpen(false);
    closeMobileNav();
  }, [location.pathname]);

  /** Same-route tap: scroll to top + close mobile menu (capture phase — works for all internal links). */
  useEffect(() => {
    const onDocClick = (e) => {
      const anchor = e.target.closest('a[href]');
      if (!anchor || anchor.target === '_blank') return;
      const raw = anchor.getAttribute('href') || '';
      if (!raw || raw.startsWith('#') || /^https?:/i.test(raw) || raw.startsWith('mailto:') || raw.startsWith('tel:')) {
        return;
      }
      let path = raw;
      let linkSearch = '';
      try {
        const url = new URL(raw, window.location.origin);
        if (url.origin !== window.location.origin) return;
        path = url.pathname;
        linkSearch = url.search;
      } catch {
        const parsed = parseInternalNavTarget(raw);
        path = parsed.pathname;
        linkSearch = parsed.search;
      }
      const sameRoute = isSameNavRoute(location.pathname, path, {
        end: path === '/',
        search: linkSearch,
        currentSearch: location.search,
      });
      if (!sameRoute) return;
      e.preventDefault();
      closeMobileNav();
      scrollToPageTop({ smooth: true });
    };
    document.addEventListener('click', onDocClick, true);
    return () => document.removeEventListener('click', onDocClick, true);
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname !== '/search') return;
    const p = new URLSearchParams(location.search).get('q') || '';
    setHeaderSearchQ(p);
  }, [location.pathname, location.search]);

  const isAdminRoute = location.pathname.startsWith('/admin');

  return (
      <>
      <ScrollToTop />
      <a href="#main-content" className="skip-to-main">
        Skip to main content
      </a>
      <div
        className={`app-bg${authReady && isAuthed ? ' app-bg--session' : ' app-bg--guest'}${isAdminRoute ? ' app-bg--admin-route' : ''}`}
      >
        <div className="site-header-fixed" ref={headerFixedRef}>
          <HomeAnnouncementBar />
          <header className="site-header-nav">
            <div
              className={`site-header-shell${headerCompact ? ' site-header-shell--compact' : ''}${
                authReady && isAuthed ? ' site-header-shell--authed' : ' site-header-shell--guest'
              }${isAdminRoute ? ' site-header-shell--admin-route' : ''}`}
            >
              <nav
                className="navbar navbar-expand-lg navbar-light site-navbar"
                aria-label="Main navigation"
              >
                <div className="container-fluid gx-3 gx-sm-4 align-items-center site-header-navbar-inner">
                  <Link
                    className="navbar-brand d-flex align-items-center me-0 me-lg-2"
                    to="/"
                    onClick={onNavLinkClick('/', { end: true })}
                  >
                    <img
                      src={brandLogo}
                      alt={`${brandName} Logo`}
                      className="navbar-logo me-2"
                    />
                    <span className="brand-text">{brandName}</span>
                  </Link>
                  <button
                    className="navbar-toggler border-0"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#navbarNav"
                    aria-controls="navbarNav"
                    aria-expanded="false"
                    aria-label="Toggle navigation"
                  >
                    <span className="navbar-toggler-icon" />
                  </button>
                  <div
                    className="collapse navbar-collapse site-header-navbar-collapse"
                    id="navbarNav"
                  >
                    <ul
                      className={`navbar-nav site-navbar-nav mx-lg-0 justify-content-lg-center mb-2 mb-lg-0 align-items-lg-center text-center text-lg-start ${
                        authReady && isAuthed ? 'py-0' : 'py-2 py-lg-0'
                      }`}
                    >
                      <li className="nav-item">
                        <NavLink className="nav-link" to="/" end onClick={onNavLinkClick('/', { end: true })}>
                          Home
                        </NavLink>
                      </li>
                      <ShopMobileNav onNavLinkClick={onNavLinkClick} />
                      <li
                        className="nav-item dropdown d-none d-lg-block"
                        ref={shopRef}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                      >
                        <NavLink
                          to="/shop"
                          className={({ isActive }) =>
                            `nav-link dropdown-toggle${isActive ? ' active' : ''}`
                          }
                          id="shopDropdown"
                          role="button"
                          aria-expanded={shopOpen}
                          onClick={onNavLinkClick('/shop')}
                        >
                          Shop
                        </NavLink>
                        <ul
                          className={`dropdown-menu shop-mega-menu-dropdown${shopOpen ? ' show' : ''}`}
                          aria-labelledby="shopDropdown"
                        >
                          <ShopMegaMenu onNavigate={() => setShopOpen(false)} />
                        </ul>
                      </li>
                      <li className="nav-item">
                        <NavLink className="nav-link" to="/about" onClick={onNavLinkClick('/about')}>
                          About
                        </NavLink>
                      </li>
                      <li className="nav-item">
                        <NavLink className="nav-link" to="/contact" onClick={onNavLinkClick('/contact')}>
                          Contact
                        </NavLink>
                      </li>
                      {authReady && !isAuthed ? (
                        <li className="nav-item d-lg-none">
                          <NavLink className="nav-link" to="/track-order" onClick={onNavLinkClick('/track-order')}>
                            Track order
                          </NavLink>
                        </li>
                      ) : null}
                    </ul>
                    <div className="site-header-toolbar d-flex flex-wrap align-items-center justify-content-center justify-content-lg-end gap-2 gap-lg-2 w-100 w-lg-auto">
                      <SiteHeaderSearch
                        headerSearchQ={headerSearchQ}
                        setHeaderSearchQ={setHeaderSearchQ}
                        onAfterSubmit={closeMobileNav}
                        onSubmit={(e) => {
                          e.preventDefault();
                          const q = headerSearchQ.trim();
                          if (q) navigate(`/search?q=${encodeURIComponent(q)}`);
                        }}
                      />
                      <div className="site-header-toolbar-actions d-flex align-items-center gap-1 gap-lg-2 flex-shrink-0 flex-wrap">
                      <div className="site-header-toolbar-divider d-none d-lg-block" aria-hidden="true" />
                      <p className="site-header-mobile-utils-label d-lg-none">Quick links</p>
                      <SiteHeaderWishlistLink onClick={onNavLinkClick('/wishlist')} />
                      <SiteHeaderCartLink onClick={onNavLinkClick('/cart')} />
                      <div className="site-header-social site-header-social--toolbar d-none d-lg-flex align-items-center">
                        <a
                          href={site.instagram_url || 'https://instagram.com/floraljewelry_salonidesigns'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="site-header-social-link"
                          title="Instagram"
                          aria-label={`${brandName} on Instagram`}
                        >
                          <img
                            src="https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/instagram.svg"
                            alt=""
                            className="site-header-social-icon site-header-social-icon--instagram"
                            width={26}
                            height={26}
                          />
                        </a>
                        <a
                          href={site.whatsapp_href || 'https://wa.me/919680384332'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="site-header-social-link"
                          title="WhatsApp"
                          aria-label="Contact on WhatsApp"
                        >
                          <img
                            src="https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/whatsapp.svg"
                            alt=""
                            className="site-header-social-icon site-header-social-icon--whatsapp"
                            width={26}
                            height={26}
                          />
                        </a>
                      </div>
                      {!authReady ? (
                        <span className="small text-muted px-2" aria-hidden>
                          …
                        </span>
                      ) : isAuthed ? (
                        <SiteHeaderAccountMenu
                          isAdmin={isAdmin}
                          logout={logout}
                          pathname={location.pathname}
                          onNavLinkClick={onNavLinkClick}
                        />
                      ) : (
                        <SiteHeaderSignInLink
                          pathname={location.pathname}
                          search={location.search}
                          onClick={onNavLinkClick('/login')}
                        />
                      )}
                      </div>
                    </div>
                  </div>
                </div>
              </nav>
            </div>
          </header>
        </div>

        <PostLoginWelcomeToast />
        <CartAddedToast />

        {/* Main Content */}
        <main
          id="main-content"
          tabIndex={-1}
          className={`main-content store-scope${location.pathname.startsWith('/admin') ? ' main-content--admin' : ''}`}
        >
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/track-order" element={<TrackOrderPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/account/invoice/:orderNumber" element={<OrderInvoicePage />} />
            <Route path="/policies/:slug" element={<PolicyPage />} />
            <Route path="/search" element={<ProductSearch />} />
            <Route path="/gift-finder" element={<GiftFinderRoute />} />
            <Route path="/product/:categorySlug/:productId" element={<ProductDetailPage />} />
            <Route path="/custom-jewelry" element={<CustomJewelryPage />} />
            <Route path="/shop" element={<Shop />} />
            {legacyRedirectRoutePaths().map((path) => (
              <Route key={path} path={path} element={<LegacyUrlRedirect />} />
            ))}
            <Route path="/shop/:categorySlug/:groupKey" element={<DynamicCategoryList />} />
            <Route path="/shop/:categorySlug" element={<DynamicCategoryHub />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/wishlist" element={<WishlistPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/order-success/:orderNumber" element={<OrderSuccessPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/404" element={<NotFound />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>

        <footer className="site-footer" role="contentinfo">
          <div className="container site-footer-container">
            <div className="site-footer-grid">
              <div className="site-footer-col site-footer-col--brand">
                <Link
                  to="/"
                  className="site-footer-brand-block text-decoration-none"
                  onClick={onNavLinkClick('/', { end: true })}
                >
                  <img
                    src={brandLogo}
                    alt=""
                    className="footer-logo"
                    aria-hidden="true"
                  />
                  <div className="site-footer-brand-text">
                    <span className="footer-brand">{brandName}</span>
                    <span className="site-footer-tagline">{site.footer_tagline || 'Handcrafted Rakhi & Lumba'}</span>
                    <span className="site-footer-location">{site.footer_location || 'Ahmedabad, India'}</span>
                  </div>
                </Link>
              </div>
              <div className="site-footer-col">
                <h2 className="site-footer-heading">{footerCopy.explore_heading}</h2>
                <ul className="site-footer-list">
                  <li>
                    <NavLink to="/" end className="site-footer-list-link" onClick={onNavLinkClick('/', { end: true })}>
                      Home
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/about" className="site-footer-list-link" onClick={onNavLinkClick('/about')}>
                      About
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/shop" className="site-footer-list-link" onClick={onNavLinkClick('/shop')}>
                      Shop
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/search" className="site-footer-list-link" onClick={onNavLinkClick('/search')}>
                      Search products
                    </NavLink>
                  </li>
                  {isGiftFinderLinkVisible(site.store_copy, 'footer') ? (
                    <li>
                      <NavLink to="/gift-finder" className="site-footer-list-link" onClick={onNavLinkClick('/gift-finder')}>
                        {site.store_copy?.gift_finder?.nav_label || 'Gift finder'}
                      </NavLink>
                    </li>
                  ) : null}
                  {isCustomJewelryLinkVisible(site.store_copy, 'footer') ? (
                    <li>
                      <NavLink to="/custom-jewelry" className="site-footer-list-link" onClick={onNavLinkClick('/custom-jewelry')}>
                        {site.store_copy?.custom_jewelry?.nav_label || 'Custom jewelry design'}
                      </NavLink>
                    </li>
                  ) : null}
                  <li>
                    <NavLink to="/contact" className="site-footer-list-link" onClick={onNavLinkClick('/contact')}>
                      Contact
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/track-order" className="site-footer-list-link" onClick={onNavLinkClick('/track-order')}>
                      Track order
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/blog" className="site-footer-list-link" onClick={onNavLinkClick('/blog')}>
                      Styling ideas
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/policies/shipping" className="site-footer-list-link" onClick={onNavLinkClick('/policies/shipping')}>
                      Shipping
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/policies/returns" className="site-footer-list-link" onClick={onNavLinkClick('/policies/returns')}>
                      Returns
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/policies/privacy" className="site-footer-list-link" onClick={onNavLinkClick('/policies/privacy')}>
                      Privacy
                    </NavLink>
                  </li>
                </ul>
              </div>
              <div className="site-footer-col">
                <h2 className="site-footer-heading">{footerCopy.connect_heading}</h2>
                <ul className="site-footer-list site-footer-list--connect">
                  <li>
                    <a href={site.tel_href || 'tel:+919680384332'} className="site-footer-list-link">
                      {site.phone_display || '+91 9680384332'}
                    </a>
                  </li>
                  <li>
                    <a
                      href={site.email_href || 'mailto:salonidesigns.floral@gmail.com'}
                      className="site-footer-list-link site-footer-list-link--wrap"
                    >
                      {site.email || 'salonidesigns.floral@gmail.com'}
                    </a>
                  </li>
                  <li className="site-footer-social-row">
                    <a
                      href={site.instagram_url || 'https://instagram.com/floraljewelry_salonidesigns'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="site-footer-pill-link"
                      aria-label="Instagram"
                    >
                      Instagram
                    </a>
                    <a
                      href={site.whatsapp_href || 'https://wa.me/919680384332'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="site-footer-pill-link"
                      aria-label="WhatsApp"
                    >
                      WhatsApp
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            <div className="site-footer-trust">
              <span>{store.footer_made_in || 'Handcrafted in India'}</span>
              <span aria-hidden> · </span>
              <span>{store.trust_badge_2 || 'Secure checkout'}</span>
              <span aria-hidden> · </span>
              <Link to="/policies/shipping" onClick={onNavLinkClick('/policies/shipping')}>{footerCopy.shipping_link_label}</Link>
            </div>
            <div className="site-footer-meta">
              <span className="site-footer-copy">
                &copy; {new Date().getFullYear()} {brandName}. {footerCopy.copyright_suffix}
              </span>
              {!authReady ? null : isAuthed ? (
                <>
                  <Link to="/account" className="site-footer-admin" onClick={onNavLinkClick('/account')}>
                    Account
                  </Link>
                  {isAdmin ? (
                    <>
                      <span className="site-footer-admin-sep" aria-hidden>
                        {' · '}
                      </span>
                      <Link to="/admin" className="site-footer-admin" onClick={onNavLinkClick('/admin')}>
                        Store catalog
                      </Link>
                    </>
                  ) : null}
                </>
              ) : (
                <Link
                  to={loginUrlWithReturn(location.pathname, location.search)}
                  className="site-footer-admin"
                  onClick={onNavLinkClick('/login')}
                >
                  Sign in
                </Link>
              )}
            </div>
          </div>
        </footer>
      </div>
      </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <AuthProvider>
          <CartProvider>
            <WishlistProvider>
              <SiteSettingsProvider>
                <AppInner />
              </SiteSettingsProvider>
            </WishlistProvider>
          </CartProvider>
        </AuthProvider>
      </Router>
    </HelmetProvider>
  );
}

export default App;
